"use client";

import { useEffect, useState } from "react";

import Image from "next/image";
import Link from "next/link";

import { Menu, X } from "lucide-react";
import { useTranslations } from "next-intl";

import folktaleMark from "@/assets/folktale-mark.png";
import { cn } from "@/lib/utils";

import { SITE_LINKS } from "./links";

const NAV = [
  { href: SITE_LINKS.product, key: "product" },
  { href: SITE_LINKS.solutions, key: "solutions" },
  { href: SITE_LINKS.resources, key: "resources" },
] as const;

export function SiteBrand() {
  const translateText = useTranslations("website");
  return (
    <Link
      href={SITE_LINKS.home}
      className="flex items-center gap-2.5"
      aria-label={translateText("homeAria")}
    >
      <Image
        src={folktaleMark}
        alt=""
        className="h-7 w-7 rounded-md object-contain"
      />
      <span className="font-site-display text-xl font-medium tracking-tight">
        {translateText("brand")}
      </span>
    </Link>
  );
}

export default function SiteHeader() {
  const translateText = useTranslations("website.nav");
  const translateSite = useTranslations("website");
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full border-b bg-site-bone/85 backdrop-blur-md transition-all duration-200",
        scrolled ? "border-site-border" : "border-transparent",
      )}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
        <SiteBrand />

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map((item) => (
            <a
              key={item.key}
              href={item.href}
              className="rounded-md px-3.5 py-2 text-sm font-medium text-site-ink/75 transition-colors hover:bg-site-cream hover:text-site-ink"
            >
              {translateText(item.key)}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <a
            href={SITE_LINKS.signIn}
            className="rounded-md px-3 py-2 text-sm font-medium text-site-ink/75 transition-colors hover:text-site-ink"
          >
            {translateText("signIn")}
          </a>
          <a
            href={SITE_LINKS.contact}
            className="inline-flex h-8 items-center rounded-md border border-site-ink px-3 text-sm font-medium transition-colors hover:bg-site-ink hover:text-site-bone"
          >
            {translateText("speakToSales")}
          </a>
          <Link
            href={SITE_LINKS.startFree}
            className="inline-flex h-8 items-center rounded-md bg-site-forest px-3 text-sm font-medium text-site-bone transition-colors hover:bg-site-terracotta"
          >
            {translateText("startForFree")}
          </Link>
        </div>

        <button
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-site-cream lg:hidden"
          onClick={() => setOpen((value) => !value)}
          aria-label={translateSite("toggleMenu")}
          aria-expanded={open}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open ? (
        <div className="border-t border-site-border bg-site-bone lg:hidden">
          <div className="space-y-1 px-5 py-4">
            {NAV.map((item) => (
              <a
                key={item.key}
                href={item.href}
                onClick={() => setOpen(false)}
                className="block rounded-md px-3 py-2.5 text-base font-medium text-site-ink/80 hover:bg-site-cream"
              >
                {translateText(item.key)}
              </a>
            ))}
            <div className="flex flex-col gap-2 pt-3">
              <a
                href={SITE_LINKS.signIn}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2.5 text-base font-medium text-site-ink/80 hover:bg-site-cream"
              >
                {translateText("signIn")}
              </a>
              <a
                href={SITE_LINKS.contact}
                onClick={() => setOpen(false)}
                className="inline-flex h-10 items-center justify-center rounded-md border border-site-ink text-sm font-medium"
              >
                {translateText("speakToSales")}
              </a>
              <Link
                href={SITE_LINKS.startFree}
                onClick={() => setOpen(false)}
                className="inline-flex h-10 items-center justify-center rounded-md bg-site-forest text-sm font-medium text-site-bone"
              >
                {translateText("startForFree")}
              </Link>
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
}
